import { useEffect, useState } from "react";
import { IdentificationCard, UploadSimple, DownloadSimple, CircleNotch, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import BadgePhotoEditor from "./BadgePhotoEditor";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function BadgeCard({ emp, uid, isAdmin, onChanged }) {
  const [data, setData] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const LEGACY_LABELS = { plain: "Standard (legacy)", aed: "CPR/AED (legacy)" };
  const HIDDEN_BUILTIN = ["plain", "aed"];
  const [templates, setTemplates] = useState([
    { key: "dcw", label: "Direct Care Worker (readable)" },
    { key: "modern", label: "Modern" },
    { key: "modern_aed", label: "Modern with Heartsaver CPR/AED" },
    { key: "probationary", label: "Probationary (Modern)" },
    { key: "service", label: "Years of Service (Modern)" },
  ]);
  const [variant, setVariant] = useState("dcw");
  const [name, setName] = useState("");
  const [empId, setEmpId] = useState("");
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ label: "", layout: "plain" });
  const [ribbon, setRibbon] = useState("");

  const load = () => api.get(`/employees/${uid}/badges`).then((r) => setData(r.data)).catch(() => setData({ badges: [], has_photo: false }));
  useEffect(() => {
    if (!isAdmin) return;
    load();
    api.get("/badges/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    setName(emp.name || "");
    const pid = emp.profile?.employee_id || "";
    const tov = pid.match(/^TOV-(\d+)$/i);
    setEmpId(tov ? String(99000 + Number(tov[1])) : pid);
    const sy = (emp.profile?.hire_date || emp.profile?.start_date || "").slice(0, 4);
    setYear(/^\d{4}$/.test(sy) ? sy : String(new Date().getFullYear()));
    const sd = (emp.profile?.hire_date || emp.profile?.start_date || "").slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(sd)) {
      const started = new Date(sd);
      const now = new Date();
      let n = now.getFullYear() - started.getFullYear();
      if (now.getMonth() < started.getMonth() || (now.getMonth() === started.getMonth() && now.getDate() < started.getDate())) n -= 1;
      setRibbon(n >= 1 ? `${n} YEARS OF SERVICE` : "FIRST YEAR OF SERVICE");
    }
    // eslint-disable-next-line
  }, [uid, isAdmin, emp.name]);

  if (!isAdmin) return null;

  const uploadTemplate = async (f) => {
    if (!f) return;
    if (!newTpl.label.trim()) { toast.error("Name the template version first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("label", newTpl.label.trim());
      fd.append("layout", newTpl.layout);
      const { data } = await api.post("/badges/templates", fd);
      toast.success(`Template version "${data.template.label}" added`);
      setTemplates((t) => [...t, data.template]);
      setVariant(data.template.key);
      setNewTpl({ label: "", layout: "plain" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("employee_id", empId);
      fd.append("year", year);
      fd.append("variant", variant);
      if (variant === "service") fd.append("ribbon", ribbon);
      await api.post(`/employees/${uid}/badge`, fd);
      toast.success("Badge generated — download it below");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const labelFor = (key) => (templates.find((t) => t.key === key)?.label) || LEGACY_LABELS[key] || key;

  const download = async (bid, fmt) => {
    try {
      const r = await api.get(`/employees/${uid}/badges/${bid}/file`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = fmt === "duplex" ? "pdf" : fmt;
      a.download = `badge-${(name || "employee").replace(/\s+/g, "-")}-${year}${fmt === "duplex" ? "-front-and-back" : ""}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const downloadBack = async (fmt) => {
    try {
      const r = await api.get("/badges/back", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `badge-back.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="badge-card">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 text-left" data-testid="badge-expand-toggle">
        <IdentificationCard size={18} className="text-primary" />
        <p className="font-heading text-base font-bold text-stone-900">ID badge</p>
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-stone-800">
          {open ? <><CaretUp size={12} /> Hide badge tools</> : <><CaretDown size={12} /> Make or manage badges</>}
        </span>
      </button>

      {(data?.badges || [])[0] && !open && null}

      {open && (<>
      <p className="mt-2 text-xs text-stone-400">Print-ready CR80 card (3.375" × 2.125") on the agency template — photo, name, employee ID, and "Improving lives since" year.</p>

      <div className="mt-4 flex flex-wrap items-start gap-5">
        <div className="text-center">
          {(data?.badges || [])[0] ? (
            <>
              <img src={`${api.defaults.baseURL}/employees/${uid}/badges/${data.badges[0].id}/file?fmt=png&inline=1`} alt="Current badge"
                className="h-40 rounded-lg border border-stone-200 shadow-sm" data-testid="badge-preview" />
              <p className={`mt-1 text-[10px] font-semibold ${data.badges[0].approved === false ? "text-[#8a5d14]" : "text-[#1e8449]"}`} data-testid="badge-preview-status">
                {data.badges[0].approved === false ? "pending approval — Print center" : "current badge"}
              </p>
            </>
          ) : (
            <div className="flex h-32 w-52 items-center justify-center rounded-lg border border-dashed border-stone-300 text-xs text-stone-400" data-testid="badge-no-preview">No badge yet</div>
          )}
        </div>

        <div className="text-center">
          {emp.documents?.badge_photo ? (
            <img src={`${api.defaults.baseURL}/users/${uid}/documents/badge_photo`} alt="Badge"
              className="h-20 w-20 rounded-xl border border-stone-200 object-cover" data-testid="badge-current-photo" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-stone-300 text-xs text-stone-400" data-testid="badge-no-photo">No photo</div>
          )}
          <button onClick={() => setEditorOpen(true)} className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-full border border-stone-300 px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="badge-photo-upload">
            <UploadSimple size={12} /> {emp.documents?.badge_photo ? "Replace photo" : "Upload photo"}
          </button>
          <BadgePhotoEditor uid={uid} open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={() => { onChanged?.(); load(); }} />
        </div>

        <div className="grid min-w-64 flex-1 grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label>Name on badge</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="border-stone-300" data-testid="badge-name-input" />
          </div>
          <div className="space-y-1">
            <Label>Employee ID</Label>
            <Input value={empId} onChange={(e) => setEmpId(e.target.value)} className="border-stone-300" data-testid="badge-empid-input" />
          </div>
          <div className="space-y-1">
            <Label>Improving lives since (year)</Label>
            <Input value={year} onChange={(e) => setYear(e.target.value)} maxLength={4} className="border-stone-300" data-testid="badge-year-input" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Template</Label>
            <select value={variant} onChange={(e) => setVariant(e.target.value)}
              className="h-10 w-full max-w-sm rounded-md border border-stone-300 bg-white px-3 text-sm"
              data-testid="badge-variant-select">
              {templates.filter((t) => !HIDDEN_BUILTIN.includes(t.key)).map((t) => (
                <option key={t.key} value={t.key} data-testid={`badge-variant-${t.key}`}>{t.label}</option>
              ))}
            </select>
            <button type="button" onClick={() => setTplOpen((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-stone-500 hover:text-primary"
              data-testid="badge-tpl-toggle">
              {tplOpen ? <CaretUp size={12} /> : <CaretDown size={12} />} {tplOpen ? "Hide template tools" : "New template version…"}
            </button>
            {tplOpen && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-stone-300 p-2.5">
                <Input value={newTpl.label} onChange={(e) => setNewTpl((s) => ({ ...s, label: e.target.value }))}
                  placeholder="New version name (e.g. 2027 badge)" className="h-8 w-44 border-stone-300 text-xs" data-testid="badge-tpl-label" />
                <select value={newTpl.layout} onChange={(e) => setNewTpl((s) => ({ ...s, layout: e.target.value }))}
                  className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="badge-tpl-layout">
                  <option value="plain">Same layout as Standard</option>
                  <option value="aed">Same layout as CPR/AED</option>
                  <option value="modern">Same layout as Modern</option>
                </select>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-stone-300 px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="badge-tpl-upload">
                  <UploadSimple size={12} /> Upload template image
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="badge-tpl-input" onChange={(e) => uploadTemplate(e.target.files?.[0])} />
                </label>
              </div>
            )}
          </div>
          {variant === "service" && (
            <div className="col-span-2 space-y-1">
              <Label>Gold ribbon text</Label>
              <Input value={ribbon} onChange={(e) => setRibbon(e.target.value.toUpperCase())} maxLength={40} className="border-stone-300" data-testid="badge-ribbon-input" />
            </div>
          )}
          <div className="col-span-2">
            <Button onClick={generate} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="badge-generate">
              {busy ? <><CircleNotch size={15} className="mr-1.5 animate-spin" /> Generating…</> : "Generate badge"}
            </Button>
            {!emp.documents?.badge_photo && <span className="ml-3 text-xs text-[#8a5d14]">Upload a photo first.</span>}
          </div>
          <div className="col-span-2 flex flex-wrap items-center gap-2 rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
            <p className="text-xs font-medium text-stone-600">Badge back (same for every badge — office line, if-found note):</p>
            <button onClick={() => downloadBack("png")} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="badge-back-dl-png"><DownloadSimple size={12} /> PNG</button>
            <button onClick={() => downloadBack("pdf")} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="badge-back-dl-pdf"><DownloadSimple size={12} /> PDF (print)</button>
          </div>
        </div>
      </div>

      {(data?.badges || []).length > 0 && (
        <div className="mt-4 border-t border-stone-100 pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Made before</p>
          <div className="mt-2 space-y-1.5">
            {data.badges.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 px-3 py-1.5" data-testid={`badge-row-${b.id}`}>
                <p className="text-xs text-stone-600">{labelFor(b.variant)} · since {b.year} · {(b.created_at || "").slice(0, 10)}</p>
                {b.approved === false ? (
                  <span className="flex items-center gap-2">
                    <span className="rounded-full bg-[#B7791F]/10 px-2 py-0.5 text-[10px] font-bold text-[#8a5d14]" data-testid={`badge-pending-${b.id}`}>needs approval</span>
                    <button onClick={async () => { await api.post(`/employees/${uid}/badges/${b.id}/approve`); toast.success("Badge approved"); load(); }}
                      className="rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold text-primary-foreground" data-testid={`badge-approve-${b.id}`}>Approve</button>
                  </span>
                ) : (
                <span className="flex gap-2">
                  <button onClick={() => download(b.id, "png")} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`badge-dl-png-${b.id}`}><DownloadSimple size={12} /> PNG</button>
                  <button onClick={() => download(b.id, "pdf")} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`badge-dl-pdf-${b.id}`}><DownloadSimple size={12} /> PDF (print)</button>
                  <button onClick={() => download(b.id, "duplex")} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`badge-dl-duplex-${b.id}`}><DownloadSimple size={12} /> Front+back PDF</button>
                </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
